import { CLARIFICATION_OPTION_MAX_HINTS } from "@/constants";
import type {
  ClarificationOptionReason,
  ClarificationRequest,
  ClarificationRewrite,
  ClarificationSlot,
  ClarificationSlotOption,
} from "@/features/chat/model/chatTypes";

/** Synthetic slot option that selects every candidate for that span. */
export const ALL_OPTION_ID = "all";
/** Synthetic slot option that turns the span into a free-text field. */
export const OTHER_OPTION_ID = "other";

/** Reasons worth telling the user about, in display order. `name_match` is implied by the label. */
const DISPLAYED_OPTION_REASONS: ClarificationOptionReason[] = [
  "learning_history",
  "intent_match",
  "question_match",
];

/**
 * Return the ranking reasons to show under an option, Learning History first.
 *
 * `learned` and a `learning_history` reason mean the same thing; either one shows the hint.
 */
export function optionHintReasons(option: {
  reasons?: ClarificationOptionReason[];
  learned?: boolean;
}): ClarificationOptionReason[] {
  const reasons = new Set(option.reasons ?? []);
  if (option.learned === true) {
    reasons.add("learning_history");
  }
  return DISPLAYED_OPTION_REASONS.filter((reason) => reasons.has(reason)).slice(
    0,
    CLARIFICATION_OPTION_MAX_HINTS,
  );
}

const SLOT_PATTERN = /\{\{slot:([^{}]+)\}\}/g;

export type TemplateSegment =
  | { kind: "text"; text: string }
  | { kind: "slot"; slotId: string };

/**
 * Split a rewrite template into literal text and slot placeholders.
 *
 * Mirrors the backend renderer so the sentence previewed here is byte-identical
 * to the one the server re-derives on submit.
 */
export function parseTemplate(template: string): TemplateSegment[] {
  const segments: TemplateSegment[] = [];
  let cursor = 0;
  // Fresh regex per call: the /g lastIndex is stateful and would leak between calls.
  const pattern = new RegExp(SLOT_PATTERN.source, "g");
  let match = pattern.exec(template);
  while (match !== null) {
    if (match.index > cursor) {
      segments.push({ kind: "text", text: template.slice(cursor, match.index) });
    }
    segments.push({ kind: "slot", slotId: match[1] });
    cursor = match.index + match[0].length;
    match = pattern.exec(template);
  }
  if (cursor < template.length) {
    segments.push({ kind: "text", text: template.slice(cursor) });
  }
  return segments;
}

/** Return the slot's real candidates, excluding the synthetic all/other entries. */
export function realSlotOptions(slot: ClarificationSlot): ClarificationSlotOption[] {
  return slot.options.filter(
    (option) => option.id !== ALL_OPTION_ID && option.id !== OTHER_OPTION_ID,
  );
}

function joinLabels(labels: string[]): string {
  if (labels.length <= 1) {
    return labels[0] ?? "";
  }
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * Return the text that replaces a slot's span for the given selection.
 *
 * Falls back to the user's original wording whenever the selection resolves to
 * nothing, so the sentence is never left with a hole.
 */
export function renderSlotSelection(
  slot: ClarificationSlot,
  selectedOptionIds: string[],
  customText: string,
): string {
  if (selectedOptionIds.includes(OTHER_OPTION_ID)) {
    return customText.trim() || slot.term;
  }
  const candidates = realSlotOptions(slot);
  const chosen = selectedOptionIds.includes(ALL_OPTION_ID)
    ? candidates
    : candidates.filter((option) => selectedOptionIds.includes(option.id));
  return joinLabels(chosen.map((option) => option.label)) || slot.term;
}

/** Substitute every slot placeholder with the selected label(s). */
export function renderRewrittenQuestion(
  rewrite: ClarificationRewrite,
  selections: Record<string, string[]>,
  customTexts: Record<string, string>,
): string {
  const slotsById = new Map(rewrite.slots.map((slot) => [slot.id, slot]));
  return parseTemplate(rewrite.template)
    .map((segment) => {
      if (segment.kind === "text") {
        return segment.text;
      }
      const slot = slotsById.get(segment.slotId);
      if (!slot) {
        return "";
      }
      const selected = selections[slot.id] ?? [slot.default_option_id];
      return renderSlotSelection(slot, selected, customTexts[slot.id] ?? "");
    })
    .join("");
}

/** Seed panel state with the pre-selected top pick for every slot. */
export function defaultSelections(rewrite: ClarificationRewrite): Record<string, string[]> {
  return Object.fromEntries(rewrite.slots.map((slot) => [slot.id, [slot.default_option_id]]));
}

/**
 * The dropdown sentence to show for a clarification — every clarification is answered inline.
 *
 * The backend always sends one now. A request saved before it did gets the same shape built
 * here: each question's term is appended after the user's sentence as a dropdown, exactly as
 * the backend does for a term the sentence does not contain.
 */
export function clarificationRewriteFor(
  request: ClarificationRequest,
  sourceQuestion: string,
): ClarificationRewrite | null {
  if (request.rewrite) {
    return request.rewrite;
  }
  if (request.questions.length === 0) {
    return null;
  }
  const source = sourceQuestion.trim();
  let template = source;
  const slots: ClarificationSlot[] = request.questions.map((question) => {
    const term = question.missing_or_ambiguous_terms?.[0] ?? question.question;
    const realOptions = question.options.filter((option) => option.option_type !== "free_text");
    const options: ClarificationSlotOption[] = realOptions.map((option) => ({
      id: option.id,
      label: option.label,
      option_type: option.option_type,
      value: option.value,
      reasons: option.reasons,
    }));
    if (realOptions.length > 1) {
      options.push({
        id: ALL_OPTION_ID,
        label: "All of them",
        option_type: "all",
        value: { option_ids: realOptions.map((option) => option.id) },
      });
    }
    const allowOther = question.allow_other !== false || realOptions.length === 0;
    if (allowOther) {
      options.push({ id: OTHER_OPTION_ID, label: "Other", option_type: "free_text" });
    }
    const placeholder = `${term}: {{slot:${question.id}}}`;
    template = template ? `${template} — ${placeholder}` : placeholder;
    return {
      id: question.id,
      term,
      start: source.length,
      end: source.length,
      options,
      default_option_id: realOptions[0]?.id ?? OTHER_OPTION_ID,
      allow_multiple: realOptions.length > 1,
      allow_other: allowOther,
    };
  });
  return { source_question: source, template, slots };
}
